import { FileDownload } from "@blocks/general/file-download-group"
import { ALL_FUELS, ARROW_DOWN, ARROW_UP, BEV, HFCEV, HYBRID, ICE, INFO, PHEV } from "@components/atoms/Icons"
import {
  EVTableContainer,
  FilterBlock,
  FloatingFilter,
  FuelTypeFilter,
  TT,
  TableWrapper,
  vehicleTypes,
} from "@components/atoms/data/Components"
import { Grid, Col } from "@components/layout/Grid"
import { VSpace } from "@components/layout/Spacer"
import { useEvVehiclePricing } from "@hooks/queries"
import { cn, tv } from "@utils/tw"
import { numberWithCommas } from "@utils/numberWithCommas"
import { EditableText, defineBlock } from "eddev/blocks"
import { useHydrating } from "eddev/routing"
import { ComponentPropsWithoutRef, forwardRef, useMemo, useRef, useState } from "react"
import { AnimatePresence, useInView } from "framer-motion"
import { useIsMobile } from "@hooks/useViewportDerived"
import { Button } from "@components/atoms/Button"
import { CarTypeFilter } from "@components/atoms/data/CarTypeFilter"

export const meta: BlockMeta = {
  title: "EV Index International comparison",
  category: "common",
  icon: "editor-table",
  keywords: ["ev index"],
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post", "data-embed"],
    multiple: false,
  },
}

type DataSet = {
  MODEL: string
  "FUEL TYPE": string
  "VEHICLE TYPE": string
  "ANCAP RATING": string
  Australia: number
  Norway: number
  "New Zealand": number
  Germany: number
  China: number
  USA: number
  "Great Britain": number
  Japan: number
  conversions: {
    Australia: number
    Norway: number
    "New Zealand": number
    Germany: number
    China: number
    USA: number
    "Great Britain": number
    Japan: number
  }
}

type SortConfig = {
  key: keyof DataSet
  direction: "ascending" | "descending"
}

export default defineBlock("data/evindex-international-comparison", (props) => {
  const data = useEvVehiclePricing()
  const hydrating = useHydrating()
  const [currentFuelType, setCurrentFuelType] = useState<("ICE" | "BEV" | "PHEV" | "Hybrid" | "HFCEV")[]>([
    "ICE",
    "BEV",
    "PHEV",
    "Hybrid",
    "HFCEV",
  ])
  const [currentVehicleType, setCurrentVehicleType] = useState<(keyof typeof vehicleTypes)[]>(["all"])

  const updateTypeFilters = (filter: "ICE" | "BEV" | "PHEV" | "Hybrid" | "HFCEV") => {
    if (currentFuelType.length === 5) {
      setCurrentFuelType([filter])
    } else {
      if (currentFuelType.includes(filter)) {
        if (currentFuelType.length === 1) {
          setCurrentFuelType(["ICE", "BEV", "PHEV", "Hybrid", "HFCEV"])
        } else {
          setCurrentFuelType(currentFuelType.filter((type) => type !== filter))
        }
      } else {
        setCurrentFuelType(currentFuelType.concat(filter))
      }
    }
  }

  const parsedData = useMemo(() => {
    if (data.data?.evVehiclePricing) {
      const parsed = data.data.evVehiclePricing.map((data) => JSON.parse(data?.data ?? ""))

      // Step 1: Group by MODEL and capture both Retail Price and Conversion Rate
      const groupedData = parsed.reduce((acc, car) => {
        const {
          MODEL,
          "Fuel Type": fuelType,
          "VEHICLE TYPE": vehicleType,
          "ANCAP Rating": ancapRating,
          Country: country,
          "Retail Price": retailPrice,
          "Conversion Rate": conversionRate,
        } = car

        // Initialize if the model is not yet in the accumulator
        if (!acc[MODEL]) {
          acc[MODEL] = {
            MODEL,
            "FUEL TYPE": fuelType,
            "VEHICLE TYPE": vehicleType,
            "ANCAP RATING": ancapRating,
            Australia: null,
            Norway: null,
            "New Zealand": null,
            Germany: null,
            China: null,
            USA: null,
            "Great Britain": null,
            conversions: {},
          }
        }

        // Add the retail price to the appropriate country
        acc[MODEL][country] = retailPrice

        // Capture the conversion rate separately
        acc[MODEL].conversions[country] = conversionRate

        return acc
      }, {})

      // Step 2: Convert the grouped data object into an array
      const result: DataSet[] = Object.values(groupedData)
      return result
    }
    return []
  }, [data.data?.evVehiclePricing])

  // State for sorting
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "MODEL",
    direction: "ascending",
  })

  const sortedData = useMemo(() => {
    if (!parsedData) return null
    const sorted = [...parsedData].sort((a, b) => {
      let compareA, compareB
      compareA = a[sortConfig.key]
      compareB = b[sortConfig.key]

      const arr = ["Australia", "Norway", "New Zealand", "Germany", "China", "USA", "Great Britain", "Japan"]
      if (arr.includes(sortConfig.key)) {
        compareA = a[sortConfig.key] ? parseInt(a[sortConfig.key] as string) : 99999999
        compareB = b[sortConfig.key] ? parseInt(b[sortConfig.key] as string) : 99999999
      }

      if (sortConfig.key === "ANCAP RATING") {
        const sortOrder: (string | undefined)[] = [
          "5 star",
          "4 star",
          "3 star",
          "2 star",
          "1 star",
          "Unrated",
          "NA",
          undefined,
        ]

        const extractInfo = (str: string | undefined): [string | undefined, number] => {
          if (typeof str === "undefined" || str === "NA") return [undefined, 0]
          const parts = str.split(", ")
          const rating: string = parts[0]
          const year: number = parts[1] ? parseInt(parts[1], 10) : 0
          return [rating, year]
        }

        const [ratingA, yearA] = extractInfo(a[sortConfig.key])
        const [ratingB, yearB] = extractInfo(b[sortConfig.key])

        // Compare by star rating first
        const ratingOrderA = sortOrder.indexOf(ratingA)
        const ratingOrderB = sortOrder.indexOf(ratingB)

        if (ratingOrderA !== ratingOrderB) {
          return sortConfig.direction === "ascending" ? ratingOrderA - ratingOrderB : ratingOrderB - ratingOrderA
        }

        // If star ratings are the same, compare by year
        if (yearA < yearB) {
          return sortConfig.direction === "ascending" ? 1 : -1
        }
        if (yearA > yearB) {
          return sortConfig.direction === "ascending" ? -1 : 1
        }

        return 0
      }

      if (compareA < compareB) {
        return sortConfig.direction === "ascending" ? -1 : 1
      }
      if (compareA > compareB) {
        return sortConfig.direction === "ascending" ? 1 : -1
      }
      return 0
    })

    if (
      ["ICE", "BEV", "PHEV", "Hybrid", "HFCEV"].every((item) =>
        currentFuelType.includes(item as "ICE" | "BEV" | "PHEV" | "Hybrid" | "HFCEV"),
      )
    ) {
      if (!currentVehicleType.includes("all")) {
        const filtered: any[] = []

        currentVehicleType.forEach((type) => {
          sorted
            .filter((item) => item["VEHICLE TYPE"] === vehicleTypes[type])
            .forEach((result) => {
              filtered.push(result)
            })
        })

        return filtered
      }

      return sorted
    }

    if (!currentVehicleType.includes("all")) {
      const filtered: any[] = []

      currentVehicleType.forEach((type) => {
        sorted
          .filter((item) => item["VEHICLE TYPE"] === vehicleTypes[type])
          .forEach((result) => {
            filtered.push(result)
          })
      })

      return filtered.filter((data) =>
        currentFuelType.includes(data["FUEL TYPE"] as "ICE" | "BEV" | "PHEV" | "Hybrid" | "HFCEV"),
      )
    }

    return sorted.filter((data) =>
      currentFuelType.includes(data["FUEL TYPE"] as "ICE" | "BEV" | "PHEV" | "Hybrid" | "HFCEV"),
    )
  }, [parsedData, sortConfig, currentVehicleType, currentFuelType])

  // Handler for sorting
  const requestSort = (key: keyof DataSet) => {
    let direction: "ascending" | "descending" = "ascending"
    if (key !== "VEHICLE TYPE") {
      direction = "descending" // Set default to descending for all other columns
    }
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "ascending" ? "descending" : "ascending" // Toggle direction if the same column is clicked again
    }
    setSortConfig({ key, direction })
  }

  const isMobile = useIsMobile()
  const [filterOpen, setFilterOpen] = useState<boolean>(false)
  const blockRef = useRef<HTMLDivElement>(null)
  const inView = useInView(blockRef, { amount: 0.5 })

  return (
    <EVTableContainer ref={blockRef}>
      <div className="grid-auto px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px]">
        <Col initial={12} laptop={6}>
          <EditableText as="p" placeholder="Enter content..." store="content" />
          <VSpace initial={6} />
        </Col>
        <Col initial={12} laptop={6} className="flex flex-col justify-end">
          {props.downloadData?.mediaItemUrl && props.downloadData?.fileSize && (
            <FileDownload
              title="Download this data"
              file={props.downloadData.mediaItemUrl}
              size={props.downloadData.fileSize}
              underline
            />
          )}
          <VSpace initial={6} />
        </Col>

        {isMobile && (
          <AnimatePresence>
            {inView && (
              <FloatingFilter open={filterOpen} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Button
                  size="tiny"
                  color="yellow"
                  icon={filterOpen ? "CARET_DOWN" : "CARET_UP"}
                  iconStyle="right"
                  onClick={() => setFilterOpen(!filterOpen)}
                >
                  Filter this data
                </Button>

                <FuelTypeFilter>
                  <ul>
                    <li>
                      <button
                        className={`${
                          ["ICE", "BEV", "PHEV", "Hybrid", "HFCEV"].every((item) =>
                            currentFuelType.includes(item as "ICE" | "BEV" | "PHEV" | "Hybrid" | "HFCEV"),
                          )
                            ? `active`
                            : ``
                        } all`}
                        onClick={() => setCurrentFuelType(["ICE", "BEV", "PHEV", "Hybrid", "HFCEV"])}
                      >
                        <i>{ALL_FUELS}</i>
                        <span>All fuels</span>
                      </button>
                    </li>
                    <li>
                      <button
                        className={`${currentFuelType.includes("ICE") && currentFuelType.length < 5 ? `active` : ``} ice`}
                        onClick={() => updateTypeFilters("ICE")}
                      >
                        <i>{ICE}</i>
                        <span>ICE</span>
                      </button>
                    </li>
                    <li>
                      <button
                        className={`${currentFuelType.includes("BEV") && currentFuelType.length < 5 ? `active` : ``} bev`}
                        onClick={() => updateTypeFilters("BEV")}
                      >
                        <i>{BEV}</i>
                        <span>BEV</span>
                      </button>
                    </li>
                    <li>
                      <button
                        className={`${currentFuelType.includes("PHEV") && currentFuelType.length < 5 ? `active` : ``} phev`}
                        onClick={() => updateTypeFilters("PHEV")}
                      >
                        <i>{PHEV}</i>
                        <span>PHEV</span>
                      </button>
                    </li>
                    <li>
                      <button
                        className={`${currentFuelType.includes("Hybrid") && currentFuelType.length < 5 ? `active` : ``} hybrid`}
                        onClick={() => updateTypeFilters("Hybrid")}
                      >
                        <i>{HYBRID}</i>
                        <span>Hybrid</span>
                      </button>
                    </li>
                    <li>
                      <button
                        className={`${currentFuelType.includes("HFCEV") && currentFuelType.length < 5 ? `active` : ``} hfcev`}
                        onClick={() => updateTypeFilters("HFCEV")}
                      >
                        <i>{HFCEV}</i>
                        <span>HFCEV</span>
                      </button>
                    </li>
                  </ul>
                </FuelTypeFilter>

                <CarTypeFilter currentVehicleType={currentVehicleType} setCurrentVehicleType={setCurrentVehicleType} />

                <VSpace initial={6} />
              </FloatingFilter>
            )}
          </AnimatePresence>
        )}
        {!isMobile && (
          <Col initial={12}>
            <FilterBlock>
              <FuelTypeFilter>
                <ul>
                  <li>
                    <button
                      className={`${
                        ["ICE", "BEV", "PHEV", "Hybrid", "HFCEV"].every((item) =>
                          currentFuelType.includes(item as "ICE" | "BEV" | "PHEV" | "Hybrid" | "HFCEV"),
                        )
                          ? `active`
                          : ``
                      } all`}
                      onClick={() => setCurrentFuelType(["ICE", "BEV", "PHEV", "Hybrid", "HFCEV"])}
                    >
                      <i>{ALL_FUELS}</i>
                      <span>All fuels</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={`${currentFuelType.includes("ICE") && currentFuelType.length < 5 ? `active` : ``} ice`}
                      onClick={() => updateTypeFilters("ICE")}
                    >
                      <i>{ICE}</i>
                      <span>ICE</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={`${currentFuelType.includes("BEV") && currentFuelType.length < 5 ? `active` : ``} bev`}
                      onClick={() => updateTypeFilters("BEV")}
                    >
                      <i>{BEV}</i>
                      <span>BEV</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={`${currentFuelType.includes("PHEV") && currentFuelType.length < 5 ? `active` : ``} phev`}
                      onClick={() => updateTypeFilters("PHEV")}
                    >
                      <i>{PHEV}</i>
                      <span>PHEV</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={`${currentFuelType.includes("Hybrid") && currentFuelType.length < 5 ? `active` : ``} hybrid`}
                      onClick={() => updateTypeFilters("Hybrid")}
                    >
                      <i>{HYBRID}</i>
                      <span>Hybrid</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={`${currentFuelType.includes("HFCEV") && currentFuelType.length < 5 ? `active` : ``} hfcev`}
                      onClick={() => updateTypeFilters("HFCEV")}
                    >
                      <i>{HFCEV}</i>
                      <span>HFCEV</span>
                    </button>
                  </li>
                </ul>
              </FuelTypeFilter>

              <CarTypeFilter currentVehicleType={currentVehicleType} setCurrentVehicleType={setCurrentVehicleType} />
            </FilterBlock>
          </Col>
        )}

        {!hydrating && sortedData && (
          <Col initial={12}>
            <TableWrapper>
              <TableContainer>
                <table>
                  <thead>
                    <tr>
                      <th onClick={() => requestSort("MODEL")} className="sortable">
                        <TT>
                          Model
                          {sortConfig.key === "MODEL" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("FUEL TYPE")} className="sortable">
                        <TT>
                          Fuel Type
                          {sortConfig.key === "FUEL TYPE" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("VEHICLE TYPE")} className="sortable">
                        <TT>
                          Vehicle Type
                          {sortConfig.key === "VEHICLE TYPE" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("ANCAP RATING")} className="sortable">
                        <TT>
                          ANCAP Safety Rating
                          {sortConfig.key === "ANCAP RATING" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("Australia")} className="sortable">
                        <TT>
                          Australia
                          {sortConfig.key === "Australia" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("Norway")} className="sortable">
                        <TT>
                          Norway
                          {sortConfig.key === "Norway" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("New Zealand")} className="sortable">
                        <TT>
                          New Zealand
                          {sortConfig.key === "New Zealand" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("Germany")} className="sortable">
                        <TT>
                          Germany
                          {sortConfig.key === "Germany" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("China")} className="sortable">
                        <TT>
                          China
                          {sortConfig.key === "China" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("Japan")} className="sortable">
                        <TT>
                          Japan
                          {sortConfig.key === "Japan" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("USA")} className="sortable">
                        <TT>
                          USA
                          {sortConfig.key === "USA" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("Great Britain")} className="sortable">
                        <TT>
                          Great Britain
                          {sortConfig.key === "Great Britain" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {sortedData.map((data, i) => (
                      <tr key={i}>
                        <td>{data["MODEL"]}</td>
                        <td>{data["FUEL TYPE"]}</td>
                        <td>{data["VEHICLE TYPE"]}</td>
                        <td>{formatAncap(data["ANCAP RATING"])}</td>
                        <td>
                          {data["Australia"] ? `A$${numberWithCommas(parseInt(data["Australia"].toFixed()))}` : ""}
                        </td>
                        <td>
                          {data["Norway"]
                            ? `A$${numberWithCommas(parseInt((data["Norway"] / data.conversions["Norway"]).toFixed()))} (kr${numberWithCommas(parseInt(data["Norway"].toFixed()))})`
                            : ""}
                        </td>
                        <td>
                          {data["New Zealand"]
                            ? `A$${numberWithCommas(parseInt((data["New Zealand"] / data.conversions["New Zealand"]).toFixed()))} (NZ$${numberWithCommas(parseInt(data["New Zealand"].toFixed()))})`
                            : ""}
                        </td>
                        <td>
                          {data["Germany"]
                            ? `A$${numberWithCommas(parseInt((data["Germany"] / data.conversions["Germany"]).toFixed()))} (€${numberWithCommas(parseInt(data["Germany"].toFixed()))})`
                            : ""}
                        </td>
                        <td>
                          {data["China"]
                            ? `A$${numberWithCommas(parseInt((data["China"] / data.conversions["China"]).toFixed()))} (¥${numberWithCommas(parseInt(data["China"].toFixed()))})`
                            : ""}
                        </td>
                        <td>
                          {data["Japan"]
                            ? `A$${numberWithCommas(parseInt((data["Japan"] / data.conversions["Japan"]).toFixed()))} (¥${numberWithCommas(parseInt(data["Japan"].toFixed()))})`
                            : ""}
                        </td>
                        <td>
                          {data["USA"]
                            ? `A$${numberWithCommas(parseInt((data["USA"] / data.conversions["USA"]).toFixed()))} (US$${numberWithCommas(parseInt(data["USA"].toFixed()))})`
                            : ""}
                        </td>
                        <td>
                          {data["Great Britain"]
                            ? `A$${numberWithCommas(parseInt((data["Great Britain"] / data.conversions["Great Britain"]).toFixed()))} (£${numberWithCommas(parseInt(data["Great Britain"].toFixed()))})`
                            : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableContainer>
            </TableWrapper>
          </Col>
        )}
        <Col initial={12} laptop={6}>
          <VSpace initial={6} />
          <EditableText as="p" placeholder="Enter footnote..." store="footnote" />
        </Col>
      </div>
    </EVTableContainer>
  )
})

const tableContainerStyles = tv({
  base: "max-h-[70vh] overflow-scroll mt-6 [&_table]:mt-0 [&_thead]:sticky [&_thead]:top-0",
})

const TableContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn(tableContainerStyles(), className)} {...props} />
))
TableContainer.displayName = "TableContainer"

const formatAncap = (rating: string) => {
  if (rating === "Unrated" || rating === "" || !rating) return "Unrated"
  if (rating === "NA") return "N/A"
  const parts = rating?.split(", ") ?? []

  let stars = ""
  switch (parts[0]) {
    case "5 star":
      stars = "★★★★★"
      break

    case "4 star":
      stars = "★★★★"
      break

    case "3 star":
      stars = "★★★"
      break

    case "2 star":
      stars = "★★"
      break

    case "1 star":
      stars = "★"
      break
    default:
      break
  }

  return (
    <>
      {stars} ({parts[1]})
    </>
  )
}
